import React, { useState } from "react";
import { X, ShieldCheck } from "lucide-react";
import toast from "react-hot-toast";
import {
  EmployeeStatus,
  EmployeeStatusLabels,
} from "../../types/staff.types";
import { ClinicStaffService } from "../../services/api/clinic.staff.service";
import { StaffStatusBadge } from "../../components/Staff/StaffStatusBadge";

interface StaffStatusModalProps {
  isOpen: boolean;
  staffId: string;
  staffName: string;
  currentStatus: EmployeeStatus | string;
  onClose: () => void;
  onSuccess: () => void;
}

export const StaffStatusModal: React.FC<StaffStatusModalProps> = ({
  isOpen,
  staffId,
  staffName,
  currentStatus,
  onClose,
  onSuccess,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<EmployeeStatus>(
    (currentStatus as EmployeeStatus) || EmployeeStatus.ACTIVE
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedStatus === currentStatus) {
      onClose();
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await ClinicStaffService.updateStaffStatus(
        staffId,
        selectedStatus
      );

      if (res.success) {
        toast.success(res.message || "Staff status updated successfully");
        onSuccess();
        onClose();
      } else {
        toast.error(res.message || "Failed to update staff status");
      }
    } catch (error) {
      console.error("Update status error:", error);
      toast.error("Error updating staff status");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-150">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Update Staff Status
              </h3>
              <p className="text-xs text-gray-500">
                Staff: <span className="font-medium text-gray-700">{staffName}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Select New Employment Status
            </label>
            <div className="grid grid-cols-2 gap-2.5">
              {Object.values(EmployeeStatus).map((status) => {
                const isSelected = selectedStatus === status;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setSelectedStatus(status)}
                    className={`flex items-center justify-between p-3 rounded-lg border text-sm font-medium transition-all ${
                      isSelected
                        ? "border-emerald-500 bg-emerald-50/40 text-emerald-900 ring-1 ring-emerald-500"
                        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <StaffStatusBadge status={status} />
                    {isSelected && (
                      <span className="w-2 h-2 rounded-full bg-emerald-600" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedStatus === EmployeeStatus.TERMINATED && (
            <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg text-xs text-rose-700">
              ⚠️ Marking staff as <strong>Terminated</strong> will disable their account access to clinic systems.
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Updating...
                </>
              ) : (
                "Save Status"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
